import {React, classNames, useEffect, Highlighter, useAsObservableSource, observer, Fragment, Popover2} from '@api'
import {HTMLDivProps} from '@blueprintjs/core'
import {Chip, Popover, Tooltip} from '@material-ui/core'
import {FilterItemState, FilterKey, FilterStore, useFilterStore, useVipLocalStore} from '@modules'
import {isNumericAttribute, StoresInstance} from '@stores'
import {FilterGroupMore} from './'
import {action, autorun, computed, observable, reaction} from 'mobx'
import * as css from './FilterGroup.css'
import {PrimaryIconButton} from 'centric-ui-components'
import {VipLocalStore} from '@modules'

export interface FilterGroupProps {
  className?: string
  title?: string
  noTitle?: boolean
  /** Force expanded behavior **/
  expanded?: boolean
  displayName?: string
  group: FilterKey
  loading?: boolean
  count?: any
  selected?: Map<string, FilterItemState>
  unassignedSpecCount?: number
  ignoreSection?: boolean
}

/// region Store

const useCreateFilterGroupStore = (props: Partial<FilterGroupProps>) => {
  const filter = useFilterStore()
  const obsProps = useAsObservableSource({filter, ...props})
  return useVipLocalStore((ws) => new FilterGroupUIStore(ws, obsProps))
}

const FilterGroupUIStoreContext = React.createContext<FilterGroupUIStore>(null)
export const useFilterGroupUIStore = () => React.useContext(FilterGroupUIStoreContext)

export class FilterGroupUIStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: { filter: FilterStore } & Partial<FilterGroupProps>) {
    super(stores)
    const {filter, group} = props
    if (filter && group) {
      filter.uiStores[group] = this
    }
  }

  @computed get groupState() {
    const {props: {group, filter: {groupFor}}} = this
    return groupFor(group)
  }

  @computed get groups() {
    return this.props.filter.state.groups
  }

  onMount = () => {
    const {groupState, props: {filter, group}, groups, vlp} = this

    if (group != 'search' && !groupState) {
      console.debug(`Creating missing group state for ${group}`)
      groups[group] = {key: group, selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), expanded: false, groupedByValue: {}}
    }

    const toDispose = [
      reaction(() => filter.selectedFilterGroup === this.props.group, selectedFromSearcher => {
        if (selectedFromSearcher) {
          setTimeout(() => {
            if (this.node && !this.isElementInView(this.node)) {
              this.node.scrollIntoViewIfNeeded(false)
            }

            filter.selectedFilterGroup = null
            this.attract = true
            setTimeout(() => this.attract = false, 10000)
          }, 50)
        }
      }),
      reaction(() => vlp?.hierarchy?.selected, () => {
        this.groupState.expanded = this.groupState.everExpanded = false
      }),
      autorun(() => {
        this.groupState.noSearchResults = this.noSearchResults
      })
    ]
    return () => toDispose.forEach(d => d())
  }

  @computed get noSearchResults() {
    // For boolean fields the name can be whatever is configured so we can't limit the search in ES, do it here
    const {groupState: {def, items}, props: {filter: {state: {search}, localSearchRegex}}} = this

    if (def?.agg === 'exists' || def?.type === 'Boolean') {
      return search ? !items?.some(({name}) => name.match(localSearchRegex)) : false
    }

    return this.count === 0
  }

  isElementInView = (el) => {
    var rect = el.getBoundingClientRect()
    var elemTop = rect.top
    var elemBottom = rect.bottom

    // Only completely visible elements return true:
    var isVisible = (elemTop >= 0) && (elemBottom <= window.innerHeight)
    // Partially visible elements return true:
    //isVisible = elemTop < window.innerHeight && elemBottom >= 0;
    return isVisible
  }

  @observable attract = false
  node: HTMLElement
  setRef = node => {
    this.node = node
  }

  @computed get isNumericField() {
    return isNumericAttribute(this.field?.def)
  }

  @computed get count() {
    const {props: {filter: {search}}, catalog: {itemCount}, filter: {library}, isNumericField, groupState: {range, visibleCount, count}} = this
    return isNumericField ? (library ? undefined : itemCount)
                          : count === undefined ? undefined : search ? (count ?? visibleCount) : count
  }

  @computed get extraCount() {
    const {count, groupState: {visibleCount}} = this
    return count && visibleCount
           ? count - visibleCount
           : 0
  }

  @action toggleExpanded = () => {
    this.filter.toggleExpandedGroup(this.props.group)
  }

  @observable.ref sidebarNode
  @observable moreOpen = false
  @action toggleMoreOpen = () => {
    this.moreOpen = !this.moreOpen
  }

  @action onCloseMoreDialog = () => {
    this.moreOpen = false
  }

  @action clearSelection = () => {
    const {groupState} = this
    groupState.selected.clear()
    groupState.excluded.clear()
    this.clearFreezeIds()
  }

  @action clearFreezeIds = () => {
    const {catalog, props: {group}} = this
    if (group === 'merchOption.name') {
      catalog.settings.freezeIds = []
    }
  }

  get filter() {
    return this.props.filter
  }

  @computed get titleMatchSearch() {
    const {filter: {localSearchRegex}, props: {title}} = this
    return title && localSearchRegex ? title.match(localSearchRegex) : false
  }

  /**
   * For custom fields we still want to be able to display all fields when searching, but only if we match at least 1 real result
   */
  @computed get valueSearchResults() {
    const {groupState: {items}, filter: {hasSearchText, localSearchRegex}} = this

    if (hasSearchText && localSearchRegex && !_.isEmpty(items)) {
      return items.filter(item => item.name.match(localSearchRegex))
    }

    return items ?? []
  }

  @computed get field() {
    return this.filter.fieldByName[this.props.group]
  }

  @computed get selectedSection() {
    const {filter: {state}} = this
    return state?.section
  }

  @computed get matchesSection() {
    const {props: {ignoreSection}, filter: {workspaceConfig}, field, selectedSection, catalog} = this,
      pathField = selectedSection ? selectedSection.substring(selectedSection.lastIndexOf('.') + 1) : null

    return !selectedSection || ignoreSection
           || (field?.esIndex && (selectedSection === catalog.id || (
             selectedSection.startsWith(field.section)
             && (field.field.startsWith(pathField) || field.field.indexOf(`.${pathField}`) != -1))))
           ? true
           : workspaceConfig?.sections?.length > 0
             && selectedSection && ((selectedSection == workspaceConfig._id && !field?.section) || selectedSection == field?.section)
  }

  @computed get data() {
    return this.filter.dataFor(this.props.group)
  }

  @computed get hidden() {
    const {props: {group}, isNumericField, data, filter: {search}, groupState: {count}, titleMatchSearch, matchesSection} = this

    if (isNumericField && (data?.min?.value === data?.max?.value)) { return true }
    return group !== 'search' && (!matchesSection || (search != null && count == 0 && !titleMatchSearch))
  }

  @computed get canCollapse() {
    return this.props.group !== 'search'
  }

  @computed get isDisabled() {
    // See YFTS-9723, search will break this
    const {isNumericField, filter: {hasSearchText}, groupState: {range}} = this
    if (!hasSearchText && isNumericField && range && range?.start === null && range?.end === null) return true

    return false
  }
}

/// endregion

export const FilterGroup = observer<FilterGroupProps & HTMLDivProps, HTMLDivElement>(function FilterGroup(props, ref) {
  const obsProps = useAsObservableSource(props),
    store = useCreateFilterGroupStore(obsProps)

  useEffect(store.onMount, [])

  const {title, displayName, children, className, group, unassignedSpecCount, loading, noTitle, expanded: forceExpanded, ignoreSection, ...remainingProps} = props,
    {
      hidden, selectedSection, attract, canCollapse, field,
      groupState: {expanded, range}, isDisabled,
      count, filter: {search}, isNumericField
    } = store

  //console.log({group, count, noSearchResults})

  return (
    <FilterGroupUIStoreContext.Provider value={store}>
      {!hidden &&
      <div className={classNames(css.root, className)}
           {...remainingProps}
           data-type='FilterGroup'
           data-filter-type={field?.esIndex ? 'fk' : isNumericField ? 'number' : field?.def?.type}
           data-hidden={hidden}
           data-section={selectedSection}
           data-attract={attract === true}
           data-expanded={isDisabled ? false : forceExpanded ? true : canCollapse ? expanded == true : true}
           data-group={group}
           data-disabled={isDisabled}
           ref={store.setRef}
      >
        <FilterGroupHeader ref={ref}/>

        <ol className={css.items} data-type='FilterGroupItems' data-testid={`filterGroup-items-${group}`}>
          {children}

          <FilterGroupMoreMenuItem/>

          {!loading && search && count == 0 && <div>No results (some may be filtered)</div>}
        </ol>
      </div>}
    </FilterGroupUIStoreContext.Provider>)
}, {forwardRef: true})

const FilterGroupHeader = observer<HTMLDivProps, HTMLDivElement>(function FilterGroupHeader({}, ref) {
  const store = useFilterGroupUIStore(),
    {
      strings, toggleExpanded, clearSelection, canCollapse, isDisabled,
      groupState: {visibleCount, selected, excluded}, isNumericField,
      count, props: {title, group, noTitle, expanded: forceExpanded, ignoreSection}
    } = store


  return !noTitle && title ? <div className={css.titleBar} data-type='TitleBar' data-testid={`filterGroup-titlebar-${group}`}>
		<div ref={ref} className={css.title}>
          {/*<Tooltip2 content={title.toString()}>*/}
			<div  onClick={toggleExpanded} className={css.tooltipWrapper}>
				<FilterGroupTitleSearchable/>
				<FilterGroupSectionLabel/>
			</div>
          {/*</Tooltip2>*/}
          {count != null && !isNumericField && <span key='count' className={css.count}>({isDisabled ? 0 : visibleCount > count ? visibleCount : count})</span>}
          {(selected?.size > 0 || excluded?.size > 0) && <Fragment>
			    <Chip title={`${strings.clearSelection} (${selected.size + excluded.size})`}
                className={css.selectedChip} label={`${selected.size + excluded.size}`} onDelete={clearSelection}/>
		  </Fragment>}
		</div>
      {!forceExpanded && canCollapse &&
	   <PrimaryIconButton
		   disabled={isDisabled}
		   tooltipPosition='right'
		   tooltip={isDisabled ? 'No Values have been Assigned' : strings.toggleExpandCollapse}
		   icon={'chevron-down'}
		   className={classNames(css.expandCollapse)}
		   onClick={toggleExpanded}/>}
	</div> : null
}, {forwardRef: true})

const FilterGroupMoreMenuItem = observer(function FilterGroupMoreMenuItem() {
  const store = useFilterGroupUIStore(),
    {node, isDisabled, extraCount, moreOpen, toggleMoreOpen, onCloseMoreDialog, props: {title, group}, filter: {library}, groupState: {excluded, selected}} = store
  return extraCount > 0 &&
         <Fragment>
           {/*sidebarNode &&*/
             <Popover2 isOpen={moreOpen}
                      //usePortal={false} // YFTS-10435
                       className={css.showMorePopover}
                      onClose={onCloseMoreDialog}
                       position='right'
                       content={
                         <FilterGroupMore title={title} store={store} displayName={'abcd'} selected={selected} excluded={excluded} onClose={onCloseMoreDialog} group={group} library={library}/>
                       }
                        >
               <span className={css.extra} onClick={toggleMoreOpen}>+ {extraCount} more</span>
             </Popover2>}
         </Fragment>
})

export const FilterGroupTitleSearchable = observer<{}, HTMLDivElement>(function FilterGroupTitleSearchable({}, ref) {
  const {props: {title}, filter: {localSearchWords}} = useFilterGroupUIStore()
  return <Highlighter ref={ref} className={css.titleText} data-type='TitleText' textToHighlight={title} searchWords={localSearchWords}/>
}, {forwardRef: true})

export const FilterGroupSectionLabel = observer(function FilterGroupSectionLabel() {
  const {field, catalog, catalog: {labelForSection}, filter, filter: {localSearchWords}} = useFilterGroupUIStore()
  if (!field?.section) return null

  const {section} = field

  return <Highlighter
    className={css.sectionLabel}
    data-type='SectionLabel'
    data-section={section}
    textToHighlight={labelForSection(section, field) ?? catalog.catalogItemName}
    searchWords={localSearchWords}/>
})


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/filters/components/internal/FilterGroup.tsx